import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { toKey } from "../components/editModalFunctions";

type CustomerOpt = { key: string; name: string };

type DirectoryDeps<TForm> = {
  db: Firestore;
  auth: Auth;
  form: TForm;
  setForm: Dispatch<SetStateAction<TForm>>;
  rpCustomerOpts?: CustomerOpt[];
  setRpCustomerOpts?: Dispatch<SetStateAction<CustomerOpt[]>>;
};

export function useDirectoryLists<TForm extends { customer: string; customerKey?: string; manufacturer: string; model: string }>(
  {
    db,
    auth,
    form,
    setForm,
    rpCustomerOpts,
    setRpCustomerOpts,
  }: DirectoryDeps<TForm>
) {
  const [manufacturerList, setManufacturerList] = useState<string[]>([
    "Lenovo",
    "HP",
    "Dell",
    "Apple",
    "Asus",
  ]);
  const [newManufacturer, setNewManufacturer] = useState<string>("");
  const [showNewManufacturerInput, setShowNewManufacturerInput] = useState<boolean>(false);

  const [modelList, setModelList] = useState<string[]>([]);
  const [newModel, setNewModel] = useState<string>("");
  const [showNewModelInput, setShowNewModelInput] = useState<boolean>(false);

  const [customerList, setCustomerList] = useState<string[]>([]);
  const [newCustomer, setNewCustomer] = useState("");
  const [showNewCustomerInput, setShowNewCustomerInput] = useState(false);

  const [customerListOpts, setCustomerListOpts] = useState<CustomerOpt[]>([]);

  const manufacturerExists = useMemo(() => {
    const v = newManufacturer.trim();
    if (!v) return false;
    return manufacturerList.some((m) => toKey(m) === toKey(v));
  }, [manufacturerList, newManufacturer]);

  const modelExistsForThisManufacturer = useMemo(() => {
    const v = newModel.trim();
    if (!v) return false;
    return modelList.some((m) => toKey(m) === toKey(v));
  }, [modelList, newModel]);

  const fetchCustomers = useCallback(async (): Promise<void> => {
    if (!auth.currentUser) return;

    try {
      const qs = await getDocs(query(collection(db, "customers"), orderBy("name")));

      const opts = qs.docs.map(d => {
        const data = d.data() as any;
        return {
          key: d.id,
          name: String(data?.name ?? d.id).trim(),
        };
      });
      setCustomerListOpts(opts);

      const names = opts.map(o => o.name).filter(Boolean);
      const seen = new Set<string>();
      const unique: string[] = [];
      for (const n of [...customerList, ...names]) {
        const k = toKey(n);
        if (!seen.has(k)) { seen.add(k); unique.push(n); }
      }
      setCustomerList(unique.sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" })));
    } catch (e: any) {
      const code = e?.code || e?.name;
      if (code === "permission-denied" || code === "unauthenticated") return;
      console.error("Kunde inte hämta kunder:", e?.message || e);
      setCustomerListOpts([]);
    }
  }, [auth, customerList, db]);

  const fetchManufacturers = useCallback(async (): Promise<void> => {
    if (!auth.currentUser) return;

    try {
      const qs = await getDocs(query(collection(db, "manufacturers")));
      const names = qs.docs
        .map((d) => (d.data() as any).name as string)
        .filter(Boolean);

      const seen = new Set<string>();
      const unique: string[] = [];
      for (const n of names) {
        const k = toKey(n);
        if (!seen.has(k)) { seen.add(k); unique.push(n); }
      }

      setManufacturerList(
        unique.sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }))
      );
    } catch (e: any) {
      const code = e?.code || e?.name;
      if (code === "permission-denied" || code === "unauthenticated") {
        return;
      }
      console.error("Kunde inte hämta tillverkare:", e?.message || e);
    }
  }, [auth, db]);

  const fetchModelsFor = useCallback(async (manufacturer: string): Promise<void> => {
    if (!manufacturer) { setModelList([]); return; }
    try {
      const qs = await getDocs(
        query(collection(db, "models"), where("manufacturer", "==", manufacturer))
      );
      const names = qs.docs.map((d) => (d.data() as any)?.name as string).filter(Boolean);

      const seen = new Set<string>();
      const unique: string[] = [];
      for (const n of names) {
        const k = toKey(n);
        if (!seen.has(k)) { seen.add(k); unique.push(n); }
      }

      setModelList(unique.sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" })));
    } catch (err: any) {
      console.error("Kunde inte hämta modeller:", err.message);
    }
  }, [db]);

  const handleNewManufacturerAdd = useCallback(async (): Promise<void> => {
    const trimmed = newManufacturer.trim();
    if (!trimmed) return;
    try {
      await addDoc(collection(db, "manufacturers"), { name: trimmed });
      setManufacturerList((prev) => [...prev, trimmed]);
      setForm((prevForm) => ({ ...prevForm, manufacturer: trimmed }));
      setNewManufacturer("");
      setShowNewManufacturerInput(false);
    } catch (err: any) {
      console.error("Kunde inte lägga till tillverkare:", err.message);
      alert("Fel vid tillägg av tillverkare");
    }
  }, [db, newManufacturer, setForm]);

  const handleNewModelAdd = useCallback(async (): Promise<void> => {
    const trimmed = newModel.trim();
    const manufacturer = form.manufacturer;
    if (!trimmed || !manufacturer) return;

    const key = toKey(trimmed);

    if (modelExistsForThisManufacturer) {
      alert("Modell finns redan för tillverkare.");
      const existing = modelList.find((m) => toKey(m) === key)!;
      setForm((prev) => ({ ...prev, model: existing }));
      setNewModel("");
      setShowNewModelInput(false);
      return;
    }

    try {
      const all = await getDocs(query(collection(db, "models")));
      const hit = all.docs
        .map((d) => d.data() as any)
        .find((r) => r?.name && toKey(r.name) === key);

      if (hit) {
        const otherMan = String(hit.manufacturer || "");
        if (toKey(otherMan) !== toKey(manufacturer)) {
          const ok = window.confirm(
            `Modellen finns redan för tillverkare "${otherMan}". Vill du spara ändå för "${manufacturer}"?`
          );
          if (!ok) return;
        } else {
          alert("Modell finns redan för tillverkare.");
          const existing = hit.name as string;
          setForm((prev) => ({ ...prev, model: existing }));
          setNewModel("");
          setShowNewModelInput(false);
          await fetchModelsFor(manufacturer);
          return;
        }
      }

      await addDoc(collection(db, "models"), { manufacturer, name: trimmed });
      await fetchModelsFor(manufacturer);
      setForm((prev) => ({ ...prev, model: trimmed }));
      setNewModel("");
      setShowNewModelInput(false);
    } catch (err: any) {
      console.error("Kunde inte lägga till modell:", err.message);
      alert("Fel vid tillägg av modell");
    }
  }, [db, fetchModelsFor, form.manufacturer, modelExistsForThisManufacturer, modelList, newModel, setForm]);

  const handleNewCustomerAdd = useCallback(async (): Promise<void> => {
    const trimmed = (newCustomer || "").trim();
    if (!trimmed) return;

    const slug = toKey(trimmed);

    const fromOpts = Array.isArray(customerListOpts)
      ? customerListOpts.find(o => toKey(o.name) === slug)
      : undefined;
    if (fromOpts) {
      setForm(p => ({ ...p, customer: fromOpts.name, customerKey: fromOpts.key }));
      setNewCustomer("");
      setShowNewCustomerInput(false);
      return;
    }

    const fromRp = Array.isArray(rpCustomerOpts)
      ? rpCustomerOpts.find(o => toKey(o.name) === slug)
      : undefined;
    if (fromRp) {
      setForm(p => ({ ...p, customer: fromRp.name, customerKey: fromRp.key }));
      setNewCustomer("");
      setShowNewCustomerInput(false);
      return;
    }

    if (customerList.some(n => toKey(n) === slug)) {
      try { await fetchCustomers?.(); } catch { /* ignorera, bäst-effort refresh */ }
      const after = Array.isArray(customerListOpts)
        ? customerListOpts.find(o => toKey(o.name) === slug)
        : undefined;
      setForm(p => ({ ...p, customer: trimmed, customerKey: after?.key ?? (p as any).customerKey ?? "" }));
      setNewCustomer("");
      setShowNewCustomerInput(false);
      return;
    }

    try {
      const qs = await getDocs(query(collection(db, "customers")));
      const remoteHit = qs.docs
        .map(d => ({ id: d.id, name: String((d.data() as any)?.name || "") }))
        .find(r => toKey(r.name) === slug);

      if (remoteHit) {
        setCustomerListOpts(prev => {
          const next = [...prev, { key: remoteHit.id, name: remoteHit.name }];
          next.sort((a, b) => a.name.localeCompare(b.name, "sv", { sensitivity: "base" }));
          return Array.from(new Map(next.map(x => [x.key, x])).values());
        });
        setRpCustomerOpts?.(prev => (Array.isArray(prev) ? [...prev, { key: remoteHit.id, name: remoteHit.name }] : [{ key: remoteHit.id, name: remoteHit.name }]));
        setCustomerList(prev => {
          const next = Array.from(new Set([...prev, remoteHit.name]));
          next.sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }));
          return next;
        });

        setForm(p => ({ ...p, customer: remoteHit.name, customerKey: remoteHit.id }));
        setNewCustomer(""); setShowNewCustomerInput(false);
        return;
      }
    } catch { /* tyst “ vi försöker skapa i nästa steg */ }

    try {
      const ref = await addDoc(collection(db, "customers"), {
        name: trimmed,
      });
      const id = ref.id;

      setCustomerListOpts(prev => {
        const next = [...prev, { key: id, name: trimmed }];
        next.sort((a, b) => a.name.localeCompare(b.name, "sv", { sensitivity: "base" }));
        return Array.from(new Map(next.map(x => [x.key, x])).values());
      });
      setRpCustomerOpts?.(prev => (Array.isArray(prev) ? [...prev, { key: id, name: trimmed }] : [{ key: id, name: trimmed }]));
      setCustomerList(prev => {
        const next = Array.from(new Set([...prev, trimmed]));
        next.sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }));
        return next;
      });

      setForm(p => ({ ...p, customer: trimmed, customerKey: id }));
      setNewCustomer("");
      setShowNewCustomerInput(false);
    } catch (e) {
      console.error("Fel vid tillägg av kund:", e);
      alert("Fel vid tillägg av kund");
    }
  }, [customerList, customerListOpts, db, fetchCustomers, newCustomer, rpCustomerOpts, setForm, setRpCustomerOpts]);

  return {
    manufacturerList,
    setManufacturerList,
    newManufacturer,
    setNewManufacturer,
    showNewManufacturerInput,
    setShowNewManufacturerInput,

    modelList,
    setModelList,
    newModel,
    setNewModel,
    showNewModelInput,
    setShowNewModelInput,

    customerList,
    setCustomerList,
    newCustomer,
    setNewCustomer,
    showNewCustomerInput,
    setShowNewCustomerInput,
    customerListOpts,
    setCustomerListOpts,

    fetchCustomers,
    fetchManufacturers,
    fetchModelsFor,
    handleNewManufacturerAdd,
    handleNewModelAdd,
    handleNewCustomerAdd,
    manufacturerExists,
    modelExistsForThisManufacturer,
  };
}
